<template>
<div class="coupon-codes">
  <h1>Add new coupon</h1>
  <p>Coupon codes are used in the store to track where customers come from and give them special deals.</p>

  <el-inline :marginTop="true">
    <div class="coupon-codes-input-title">Title</div>
    <el-input
      class="coupon-codes-input-input"
      placeholder="e.g. Clinic X"
      v-model="newCoupon.title"
      />
  </el-inline>

  <el-inline :marginTop="true">
    <div class="coupon-codes-input-title">Description</div>
    <el-input
      class="coupon-codes-input-input"
      v-model="newCoupon.description"
      />
  </el-inline>

  <el-inline :marginTop="true">
    <div class="coupon-codes-input-title">Price [kr]</div>
    <el-input
      class="coupon-codes-input-input"
      v-model="newCoupon.price"
      :warning="!isNumber(newCoupon.price)"
      />
  </el-inline>

  <el-inline :marginTop="true">
    <div class="coupon-codes-input-title">Code</div>
    <el-input
      class="coupon-codes-input-input"
      v-model="newCoupon.code"
      />
  </el-inline>

  <el-button
    @click="addCoupon"
    :disabled="missingNewCouponInfo"
    :tooltip="missingNewCouponInfo ? 'Invalid data' : ''"
    :marginTop="true"
    >
    Add coupon
  </el-button>

  <el-delimiter />

  <div
    v-for="coupon in coupons"
    :key="coupon.id"
    class="coupon-wrapper animation"
    @click="activeCoupon = coupon.id"
    >

    <el-inline style="align-items: flex-end; justify-content: space-between; flex-wrap: nowrap;">
      <h2 class="customer-name">{{coupon.title}}</h2>
      <div
        class="customer-order-status"
        :class="{
          'customer-order-warning': coupon.status === 0,
          'customer-order-success': coupon.status === 1
        }"
        >
        {{getStatus(coupon.status)}}
      </div>
    </el-inline>

    <!-- Detailed info -->
    <transition name="slide-fade">
      <p v-if="coupon.id === activeCoupon">
        {{coupon.description}}
      </p>
    </transition>
    <transition name="slide-fade">
      <el-inline
        :marginTop="true"
        class="coupon-data-wrapper"
        v-if="coupon.id === activeCoupon"
        >
        <div class="coupon-data">
          <b>{{coupon.price}} kr</b>
          <i>Price</i>
        </div>
      </el-inline>
    </transition>

    <!-- Summary -->
    <el-inline :marginTop="true" class="coupon-data-wrapper">
      <div class="coupon-data">
        <b>{{getRevenue(coupon.price, coupon.quantity)}} kr</b>
        <i>Revenue</i>
      </div>
      <div class="coupon-data">
        <b>{{coupon.code}}</b>
        <i>Code</i>
      </div>
      <div class="coupon-data">
        <b>{{getDuration(coupon.start, coupon.end)}} days</b>
        <i>Duration</i>
      </div>
    </el-inline>

    <!-- Expanded data -->
    <transition name="slide-fade">
      <el-inline
        :marginTop="true"
        class="coupon-data-wrapper"
        v-if="coupon.id === activeCoupon"
        >
        <div class="coupon-data">
          <b>{{coupon.quantity}}</b>
          <i>Times used</i>
        </div>
        <div class="coupon-data">
          <b>{{(new Date(coupon.start)).toDateString()}}</b>
          <i>Start date</i>
        </div>
        <div class="coupon-data">
          <b>{{coupon.end ? (new Date(coupon.end)).toDateString() : '-'}}</b>
          <i>End date</i>
        </div>
      </el-inline>
    </transition>

    <transition name="slide-fade">
      <el-inline
        :marginTop="true"
        v-if="coupon.id === activeCoupon"
        >
        <el-button
          :danger="true"
          @click="confirmCancelId = coupon.id"
          v-if="coupon.status === 1"
          :marginTop="true"
          :marginRight="true"
          >
          Cancel coupon
        </el-button>
        <el-button
          @click="activate(coupon.id)"
          v-else-if="coupon.status === 0"
          :marginTop="true"
          :marginRight="true"
          >
          Activate coupon
        </el-button>
        <el-button
          :secondary="true"
          @click="closeCouponMenu"
          :marginTop="true"
          :marginRight="true"
          >
          Close
        </el-button>
      </el-inline>
    </transition>

    <el-delimiter :short="true" />
  </div>

  <br>

  <!-- Confirm removal -->
  <el-modal
    v-if="confirmCancelId !== ''"
    @close="confirmCancelId = ''"
    close-button-align="right"
    >
    <h1>Confirm coupon cancelation</h1>
    <p>Are you sure you want to cancel this coupon?</p>
    <el-button
      :danger="true"
      @click="cancel(confirmCancelId)"
      >
      Cancel coupon
    </el-button>
  </el-modal>

  <!-- Error modal -->
  <el-modal
    v-if="errorMessage !== ''"
    @close="errorMessage = ''"
    close-button-align="right"
    >
    <h1>Error!</h1>
    <p>{{errorMessage}}</p>
  </el-modal>

  <!-- Status -->
  <el-sticky-footer v-if="isLoading">
    <el-message :warning="true">
      <p>Loading coupons.</p>
    </el-message>
  </el-sticky-footer>
  <el-sticky-footer v-if="isPending">
    <el-message :info="true">
      <p>Syncronizing with the server.</p>
    </el-message>
  </el-sticky-footer>
</div>
</template>
<script>

import firestore from 'services/firestore'
import logScope from 'log-scope'

const log = logScope('CouponCodes')

export default {
  data () {
    return {
      activeCoupon: null,
      newCoupon: {
        title: '',
        price: '2975',
        description: '',
        code: ''
      },
      couponOrder: [],
      isLoading: false,
      isPending: false,
      errorMessage: '',
      confirmCancelId: ''
    }
  },
  computed: {
    missingNewCouponInfo () {
      return !(this.newCoupon.title !== '' &&
        this.isNumber(this.newCoupon.price) &&
        this.newCoupon.price !== '' &&
        this.newCoupon.description !== '' &&
        this.newCoupon.code !== '')
    },
    coupons () {
      return this.$store.getters.coupons
    }
  },
  methods: {
    isNumber (str) {
      return !(isNaN(Number(str)))
    },
    async addCoupon () {
      this.isPending = true

      let coupon = {
        ...this.newCoupon,
        start: +(new Date()),
        end: null,
        status: 1,
        quantity: 0
      }

      coupon.code = coupon.code.toLowerCase()
      coupon.price = Number(coupon.price)

      try {
        await firestore.add('coupons', coupon)
        log.success('Added new coupon')
      } catch (err) {
        log.error('Could not store new coupon', err)
        this.errorMessage = 'Could not store new coupon.'
      }

      this.isPending = false
    },
    async cancel (id) {
      const status = 0
      const end = +(new Date())

      this.isPending = true
      this.confirmCancelId = ''

      try {
        await firestore.set('coupons', id, { status, end }, { merge: true })
      } catch (err) {
        log.error('Could not cancel coupon', err)
        this.errorMessage = 'Could not update coupon.'
      }

      this.isPending = false

      this.$nextTick(() => {
        this.activeCoupon = null
      })
    },
    async activate (id) {
      const status = 1
      const end = null
      this.isPending = true

      try {
        await firestore.set('coupons', id, { status, end }, { merge: true })
      } catch (err) {
        log.error('Could not set coupon status', err)
        this.errorMessage = 'Could not update coupon.'
      }

      this.isPending = false

      this.$nextTick(() => {
        this.activeCoupon = null
      })
    },
    getStatus (status) {
      return {
        0: 'Canceled',
        1: 'Active'
      }[status]
    },
    getDuration (start, end) {
      let startDate = new Date(start)
      let endDate = typeof end === 'number' ? new Date(end) : new Date()
      let diff = endDate - startDate
      let days = Math.round(diff / (1000 * 60 * 60 * 24))

      if (days < 0) {
        days = 0
      }

      return days
    },
    getRevenue (price, quantity) {
      return price * quantity
    },
    closeCouponMenu () {
      setTimeout(() => {
        this.activeCoupon = null
      }, 0)
    }
  }
}
</script>
<style lang="less" scoped>
  @import '~style/elElement/variables.less';

  .coupon-codes-input-title {
    width: 200px !important;
  }

  .coupon-codes-input-input {
    width: auto !important;
  }

  .coupon-wrapper {
    cursor: pointer;
  }

  .customer-name {
    margin-bottom: 0px;
    margin-top: 0px;
    margin-right: 10px;

    @media (max-width: 600px) {
      font-size: 18px;
    }
  }

  .customer-order-status {
    height: 30px;
    padding: 0px 10px;
    border-radius: 15px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .customer-order-warning {
    background-color: #FEECCE;
    border: 1px solid #FFC872;
    color: #805512;
  }

  .customer-order-success {
    background-color: #DFF0D8;
    border: 1px solid #43B8A9;
    color: #42A293;
  }

  .coupon-data-wrapper {
    justify-content: space-between;
  }

  .coupon-data {
    display: flex;
    flex-direction: column;
    width: auto;
    justify-content: flex-start;
    width: 30%;
    min-width: 120px;

    i {
      font-size: 12px;
    }

    @media (max-width: 500px) {
      width: 100%;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;
    }
  }

  .slide-fade-enter-active {
  transition: all .4s ease;
  }
  .slide-fade-leave-active {
    transition: all 0s ease;
  }
  .slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
    transform: translateY(-30px);
    opacity: 0;
  }
</style>
